import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Sparkles, ArrowLeft, Plus, Trash2, Save, Shield, Settings, Users, UsersRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAgents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    icon: '',
    system_prompt: '',
    ultra_system_prompt: '',
    greeting_message: '',
    usage_guide: '',
    model_provider: 'openrouter',
    model_name: 'anthropic/claude-sonnet-4-5-20250929',
    max_tokens: '',
    cost_credits: 10,
    sort_order: 0,
    status: 'active',
    allowed_roles: ['vip', 'admin'],
    enable_reasoning: false,
    reasoning_level: 'medium',
    temperature: 0.7
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/create');
      return;
    }
    fetchAgents();
    fetchOpenRouterModels();
  }, [user, navigate]);

  const fetchOpenRouterModels = async () => {
    setLoadingModels(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/openrouter-models`);
      setOpenrouterModels(response.data);
    } catch (error) {
      console.error('獲取模型列表失敗:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/agents`);
      setAgents(response.data);
    } catch (error) {
      toast.error('獲取Agent失敗');
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/agents`, formData);
      toast.success('創建成功！');
      setShowCreateForm(false);
      resetForm();
      fetchAgents();
    } catch (error) {
      toast.error('創建失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (agentId) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/agents/${agentId}`, formData);
      toast.success('更新成功！');
      setEditingAgent(null);
      resetForm();
      fetchAgents();
    } catch (error) {
      toast.error('更新失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('確定要刪除這個Agent嗎？')) return;

    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin/agents/${agentId}`);
      toast.success('刪除成功！');
      fetchAgents();
    } catch (error) {
      toast.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (agent) => {
    setEditingAgent(agent.id);
    setFormData({
      name: agent.name,
      description: agent.description,
      category: agent.category,
      icon: agent.icon,
      system_prompt: agent.system_prompt,
      ultra_system_prompt: agent.ultra_system_prompt || '',
      greeting_message: agent.greeting_message || '',
      usage_guide: agent.usage_guide || '',
      model_provider: agent.model_provider,
      model_name: agent.model_name,
      max_tokens: agent.max_tokens || '',
      cost_credits: agent.cost_credits,
      sort_order: agent.sort_order ?? 0,
      status: agent.status,
      allowed_roles: agent.allowed_roles || ['vip', 'admin'],
      enable_reasoning: agent.enable_reasoning || false,
      reasoning_level: agent.reasoning_level || 'medium',
      temperature: agent.temperature ?? 0.7
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      icon: '',
      system_prompt: '',
      ultra_system_prompt: '',
      greeting_message: '',
      usage_guide: '',
      model_provider: 'openrouter',
      model_name: 'anthropic/claude-sonnet-4-5-20250929',
      max_tokens: '',
      cost_credits: 10,
      sort_order: 0,
      status: 'active',
      allowed_roles: ['vip', 'admin'],
      enable_reasoning: false,
      reasoning_level: 'medium',
      temperature: 0.7
    });
  };


  if (user?.role !== 'admin') return null;

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900">Agent 管理</h1>
            <p className="text-neutral-600 text-sm mt-1">配置和管理 AI Agent</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/review-agents')}
              className="rounded-full"
              data-testid="manage-review-agents-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              審核 Agent
            </Button>
            <Button onClick={() => setShowCreateForm(true)} className="rounded-full" data-testid="create-agent-btn">
              <Plus className="w-4 h-4 mr-2" />
              新增 Agent
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-8">
        {/* Create Form */}
        {showCreateForm && (
          <Card className="p-6 mb-6">
            <h2 className="text-xl font-heading font-bold mb-4">新增Agent</h2>
            <AgentForm 
              formData={formData}
              setFormData={setFormData}
              onSave={handleCreate}
              onCancel={() => {
                setShowCreateForm(false);
                resetForm();
              }}
              loading={loading}
              openrouterModels={openrouterModels}
              loadingModels={loadingModels}
            />
          </Card>
        )}

        {/* Agents List */}
        <div className="space-y-6">
          {agents.map((agent) => (
            <Card key={agent.id} className="p-6">
              {editingAgent === agent.id ? (
                <div>
                  <h3 className="text-xl font-heading font-bold mb-4">編輯Agent</h3>
                  <AgentForm 
                    formData={formData}
                    setFormData={setFormData}
                    onSave={() => handleUpdate(agent.id)}
                    onCancel={() => {
                      setEditingAgent(null);
                      resetForm();
                    }}
                    loading={loading}
                    openrouterModels={openrouterModels}
                    loadingModels={loadingModels}
                    agentId={agent.id}
                  />
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{agent.icon}</span>
                      <div>
                        <h3 className="text-xl font-heading font-bold">{agent.name}</h3>
                        <p className="text-sm text-neutral-600">{agent.description}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(agent)}
                        className="rounded-full"
                        data-testid={`edit-agent-${agent.id}-btn`}
                      >
                        編輯
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(agent.id)}
                        className="rounded-full text-red-600"
                        data-testid={`delete-agent-${agent.id}-btn`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-neutral-600">類別：</span> {agent.category}
                    </div>
                    <div>
                      <span className="text-neutral-600">模型：</span> {agent.model_name}
                    </div>
                    <div>
                      <span className="text-neutral-600">消耗點數：</span> {agent.cost_credits}
                    </div>
                    <div data-testid={`agent-temperature-${agent.id}`}>
                      <span className="text-neutral-600">溫度：</span> {agent.temperature ?? 0.7}
                    </div>
                    <div>
                      <span className="text-neutral-600">狀態：</span> 
                      <span className={agent.status === 'active' ? 'text-green-600' : 'text-red-600'}>
                        {agent.status === 'active' ? '啟用' : '停用'}
                      </span>
                    </div>
                    <div>
                      <span className="text-neutral-600">自動審核：</span> 
                      <span className={agent.review_config?.review_enabled ? 'text-amber-600' : 'text-neutral-400'}>
                        {agent.review_config?.review_enabled 
                          ? `啟用 (${agent.review_config?.review_checkpoints?.length || 0} 個審核點)` 
                          : '未啟用'}
                      </span>
                    </div>
                  </div>

                  {/* Review Config & Sub-agents Quick Access */}
                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/agents/${agent.id}/subagents`)}
                      className="rounded-full text-primary-600 border-primary-200 hover:bg-primary-50"
                      data-testid={`agent-subagents-${agent.id}-btn`}
                    >
                      <UsersRound className="w-4 h-4 mr-1" />
                      Sub-agents 管理
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/agents/${agent.id}/review-config`)}
                      className="rounded-full text-amber-600 border-amber-200 hover:bg-amber-50"
                      data-testid={`agent-review-config-${agent.id}-btn`}
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      審核設定
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </main>
    </>
  );
};

const AgentForm = ({ formData, setFormData, onSave, onCancel, loading, openrouterModels, loadingModels, agentId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [generatingGuide, setGeneratingGuide] = useState(false);
  
  const filteredModels = openrouterModels.filter(model =>
    model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    model.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">基本資訊</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">名稱</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="例：文案生成器"
            />
          </div>
          <div>
            <Label htmlFor="category">類別</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              placeholder="例：copywriting"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="description">描述</Label>
          <Input
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="簡單描述這個Agent的功能"
          />
        </div>

        <div>
          <Label htmlFor="icon">Icon</Label>
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center text-2xl border border-neutral-200">
              {formData.icon || '?'}
            </div>
            <div className="flex-1">
              <Input id="icon" value={formData.icon} onChange={(e) => setFormData({...formData, icon: e.target.value})} placeholder="輸入表情符號" className="mb-1.5" />
              <div className="flex flex-wrap gap-1">
                {['✍️','🎬','📊','🎯','💡','🔍','📝','🚀','🧠','💬','📚','🎨','🏆','⚡','🔥','🌟','📈','🎭','💼','🤖'].map(e => (
                  <button key={e} type="button" onClick={() => setFormData({...formData, icon: e})}
                    className={`w-8 h-8 rounded-lg text-base hover:bg-neutral-100 transition-colors ${formData.icon === e ? 'bg-primary-100 ring-1 ring-primary-400' : ''}`}>{e}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sort_order">排序順序</Label>
            <Input id="sort_order" type="number" value={formData.sort_order}
              onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})} data-testid="agent-sort-order" />
            <p className="text-xs text-neutral-500 mt-1">數字越小排越前（0, 1, 2...）</p>
          </div>
        </div>
      </div>

      {/* Model Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">AI模型設定</h3>
        <div>
          <Label htmlFor="model_name">OpenRouter 模型 {loadingModels && '(載入中...)'}</Label>
          <div className="space-y-2">
            <Input
              placeholder="搜尋模型..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-2"
            />
            <Select 
              value={formData.model_name}
              onValueChange={(value) => setFormData({...formData, model_name: value, model_provider: 'openrouter'})}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="選擇模型..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] bg-white">
                {filteredModels.length === 0 ? (
                  <div className="p-4 text-center text-sm text-neutral-500">
                    {loadingModels ? '載入中...' : '找不到模型'}
                  </div>
                ) : (
                  filteredModels.map(model => (
                    <SelectItem 
                      key={model.id} 
                      value={model.id}
                      className="bg-white hover:bg-primary-50 focus:bg-primary-100 cursor-pointer"
                    >
                      <div className="flex flex-col py-1">
                        <span className="font-medium text-neutral-900">{model.name}</span>
                        <span className="text-xs text-neutral-500">{model.id}</span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral-500">
              已選擇：{formData.model_name}
            </p>
          </div>
        </div>

        {/* Reasoning Configuration */}
        <div className="bg-blue-50 p-4 rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-neutral-900">推理功能</h4>
              <p className="text-sm text-neutral-600">讓AI展示思考過程（適用於非推理模型如Claude Sonnet）</p>
            </div>
            <Button
              type="button"
              variant={formData.enable_reasoning ? 'default' : 'outline'}
              onClick={() => setFormData({...formData, enable_reasoning: !formData.enable_reasoning})}
              size="sm"
            >
              {formData.enable_reasoning ? '已啟用' : '停用'}
            </Button>
          </div>

          {formData.enable_reasoning && (
            <div>
              <Label>推理等級</Label>
              <Select 
                value={formData.reasoning_level}
                onValueChange={(value) => setFormData({...formData, reasoning_level: value})}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="low" className="bg-white hover:bg-primary-50">
                    <div className="flex flex-col">
                      <span className="font-medium">低 (Low)</span>
                      <span className="text-xs text-neutral-500">簡單說明思考方向（1-2句話）</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="medium" className="bg-white hover:bg-primary-50">
                    <div className="flex flex-col">
                      <span className="font-medium">中 (Medium)</span>
                      <span className="text-xs text-neutral-500">展示完整思考過程</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="high" className="bg-white hover:bg-primary-50">
                    <div className="flex flex-col">
                      <span className="font-medium">高 (High)</span>
                      <span className="text-xs text-neutral-500">詳細深度分析和推理</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="xhigh" className="bg-white hover:bg-primary-50">
                    <div className="flex flex-col">
                      <span className="font-medium">超高 (xhigh)</span>
                      <span className="text-xs text-neutral-500">Opus 4.7 獨有 · 最深層 reasoning，複雜任務必備</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-2">
                💡 提示：Gemini Flash Thinking等原生推理模型不需要啟用此功能
              </p>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="temperature">Temperature</Label>
            <Input
              id="temperature"
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={formData.temperature}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setFormData({ ...formData, temperature: Number.isNaN(value) ? 0.7 : value });
              }}
              data-testid="agent-temperature-input"
            />
            <p className="text-xs text-neutral-500 mt-1">0 = 更穩定，2 = 更有創意</p>
          </div>
          <div>
            <Label htmlFor="max_tokens">Max Output Tokens</Label>
            <Input
              id="max_tokens"
              type="number"
              min="0"
              max="200000"
              step="1000"
              value={formData.max_tokens}
              onChange={(e) => {
                const value = e.target.value === '' ? '' : parseInt(e.target.value);
                setFormData({ ...formData, max_tokens: value });
              }}
              placeholder="留空用預設值"
              data-testid="agent-max-tokens-input"
            />
            <p className="text-xs text-neutral-500 mt-1">
              留空 = 自動（一般 16K，推理 32K）
            </p>
            {(() => {
              const selectedModel = openrouterModels.find(m => m.id === formData.model_name);
              const maxComp = selectedModel?.max_completion_tokens;
              const ctxLen = selectedModel?.context_length;
              if (!selectedModel) return null;
              return (
                <div className="mt-2 p-2.5 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium mb-1">
                    {selectedModel.name} 的限制：
                  </p>
                  <div className="text-[11px] text-blue-600 space-y-0.5">
                    {maxComp > 0 && <div>Max Output Tokens → <strong>{maxComp.toLocaleString()}</strong></div>}
                    {ctxLen > 0 && <div>Context Length → <strong>{ctxLen.toLocaleString()}</strong></div>}
                    {maxComp > 0 && (
                      <button type="button" onClick={() => setFormData({ ...formData, max_tokens: maxComp })}
                        className="mt-1 text-blue-700 underline underline-offset-2 hover:text-blue-900 cursor-pointer">
                        點擊套用最大值 {maxComp.toLocaleString()}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* User Permissions */}
        <div className="border-t pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary-600" />
            <div>
              <h3 className="font-semibold text-neutral-900">用戶權限設定</h3>
              <p className="text-sm text-neutral-600">選擇哪些等級的用戶可以使用此 Agent</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: 'user', label: '普通用戶', desc: '基礎功能' },
              { value: 'vip', label: 'VIP 用戶', desc: '全部功能' },
              { value: 'operator', label: '營運者', desc: '內容管理' },
              { value: 'admin', label: '管理員', desc: '最高權限' }
            ].map((role) => (
              <div key={role.value} className="flex items-start space-x-2 p-3 border rounded-lg hover:bg-neutral-50">
                <Checkbox
                  id={`role-${role.value}`}
                  checked={formData.allowed_roles?.includes(role.value)}
                  onCheckedChange={(checked) => {
                    const currentRoles = formData.allowed_roles || [];
                    const newRoles = checked
                      ? [...currentRoles, role.value]
                      : currentRoles.filter(r => r !== role.value);
                    setFormData({ ...formData, allowed_roles: newRoles });
                  }}
                />
                <div className="grid gap-1">
                  <label
                    htmlFor={`role-${role.value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {role.label}
                  </label>
                  <p className="text-xs text-neutral-500">{role.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-amber-600 mt-3">
            💡 提示：預設只允許 VIP 和管理員使用新 Agent。勾選「普通用戶」即所有人可用。
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cost_credits">消耗點數</Label>
            <Input
              id="cost_credits"
              type="number"
              value={formData.cost_credits}
              onChange={(e) => setFormData({...formData, cost_credits: parseInt(e.target.value)})}
            />
          </div>
          <div>
            <Label htmlFor="status">狀態</Label>
            <Select 
              value={formData.status}
              onValueChange={(value) => setFormData({...formData, status: value})}
            >
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="active" className="bg-white hover:bg-primary-50">啟用</SelectItem>
                <SelectItem value="inactive" className="bg-white hover:bg-primary-50">停用</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Greeting Message */}
      <div className="space-y-2 border-t pt-6">
        <Label htmlFor="greeting_message">Agent 問候語</Label>
        <Textarea
          id="greeting_message"
          value={formData.greeting_message}
          onChange={(e) => setFormData({...formData, greeting_message: e.target.value})}
          className="min-h-[100px]"
          placeholder="輸入 Agent 的問候語...&#10;例如：你好！我是你的專屬 Instagram 內容策劃師，很高興為你服務！"
        />
        <p className="text-xs text-neutral-500">
          💡 此問候語會在用戶開始對話時顯示
        </p>
      </div>

      {/* System Prompt */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="system_prompt">系統提示詞 (System Prompt)</Label>
          <span className="text-xs text-neutral-500">一般模式使用的提示詞</span>
        </div>
        <Textarea
          id="system_prompt"
          value={formData.system_prompt}
          onChange={(e) => setFormData({...formData, system_prompt: e.target.value})}
          className="min-h-[250px] font-mono text-sm"
          placeholder="輸入系統提示詞...&#10;例如：你是一位專業的Instagram內容策劃師..."
        />
        <p className="text-xs text-neutral-500">
          💡 此提示詞用於一般模式（未開啟 Ultra 功能時）
        </p>
      </div>

      {/* Ultra System Prompt */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="ultra_system_prompt">Ultra 模式提示詞 (Ultra System Prompt)</Label>
          <span className="text-xs text-purple-600">✨ Ultra 專用</span>
        </div>
        <Textarea
          id="ultra_system_prompt"
          value={formData.ultra_system_prompt}
          onChange={(e) => setFormData({...formData, ultra_system_prompt: e.target.value})}
          className="min-h-[250px] font-mono text-sm border-purple-200 focus:border-purple-400"
          placeholder="輸入 Ultra 模式專用提示詞...&#10;例如：你是一位專業的Instagram內容策劃師。請在需要審核的內容前標記 [送審]..."
        />
        <p className="text-xs text-purple-600">
          💡 此提示詞用於 Ultra 模式（用戶開啟 Ultra 功能時）。可以包含 [送審] 等審核標記指令
        </p>
        <p className="text-xs text-neutral-500">
          ⚠️ 如果留空，Ultra 模式將使用一般模式的提示詞
        </p>
      </div>

      {/* Greeting Message */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="greeting_message">問候語 (Greeting Message)</Label>
          <span className="text-xs text-emerald-600">💬 首次對話</span>
        </div>
        <Textarea
          id="greeting_message"
          value={formData.greeting_message}
          onChange={(e) => setFormData({...formData, greeting_message: e.target.value})}
          className="min-h-[100px] text-sm border-emerald-200 focus:border-emerald-400"
          placeholder="輸入 Agent 的問候語...&#10;例如：你好！我是爆款文案生成器，我可以幫你創作吸睛的 Instagram 內容。請告訴我你想要什麼類型的文案？"
        />
        <p className="text-xs text-emerald-600">
          💡 用戶選擇此 Agent 開始新對話時，會自動顯示這段問候語
        </p>
        <p className="text-xs text-neutral-500">
          ⚠️ 如果留空，則不顯示問候語
        </p>
      </div>

      {/* Usage Guide */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="usage_guide">使用說明 (Usage Guide)</Label>
          <div className="flex items-center gap-2">
            {agentId && (
              <button type="button" disabled={generatingGuide}
                onClick={async () => {
                  setGeneratingGuide(true);
                  try {
                    const res = await axios.post(`${API_URL}/api/admin/agents/${agentId}/generate-usage-guide`);
                    setFormData({ ...formData, usage_guide: res.data.usage_guide });
                    toast.success('使用說明已生成');
                  } catch { toast.error('生成失敗'); }
                  finally { setGeneratingGuide(false); }
                }}
                className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2 disabled:opacity-50">
                {generatingGuide ? '生成中...' : '一鍵 AI 生成'}
              </button>
            )}
            <span className="text-xs text-blue-600">📖 用戶可查看</span>
          </div>
        </div>
        <Textarea
          id="usage_guide"
          value={formData.usage_guide}
          onChange={(e) => setFormData({...formData, usage_guide: e.target.value})}
          className="min-h-[120px] text-sm border-blue-200 focus:border-blue-400"
          placeholder="輸入此 Agent 的使用說明...&#10;支持 Markdown 格式。或使用「一鍵 AI 生成」自動產生。"
        />
        <p className="text-xs text-neutral-500">
          支持 Markdown。留空則不顯示使用說明按鈕。
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          onClick={onSave}
          disabled={loading}
          className="rounded-full"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? '處理中...' : '儲存'}
        </Button>
        <Button
          variant="outline"
          onClick={onCancel}
          className="rounded-full"
        >
          取消
        </Button>
      </div>
    </div>
  );
};

export default AdminAgents;